import { useCallback, useEffect, useState } from 'react';
import {
  executiveCockpitApi,
  type BusinessOpportunity,
  type EnterpriseHealth,
  type EnterpriseRisk,
  type ExecutiveAlert,
  type ExecutiveBrief,
  type ExecutiveDashboard,
  type ExecutiveDecision,
  type TimelineEvent,
} from '../../api/executivecockpit';

type Tab = 'command' | 'decisions' | 'risks' | 'opportunities' | 'approvals' | 'timeline' | 'brief' | 'health';
type Theme = 'light' | 'dark';
type Role = 'ceo' | 'cfo' | 'coo' | 'csco';

function fmtMoney(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

const ROLES: { id: Role; label: string }[] = [
  { id: 'ceo', label: 'CEO' },
  { id: 'cfo', label: 'CFO' },
  { id: 'coo', label: 'COO' },
  { id: 'csco', label: 'CSCO' },
];

export function ExecutiveCockpitPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const [role, setRole] = useState<Role>('ceo');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('command');
  const [dashboard, setDashboard] = useState<ExecutiveDashboard | null>(null);
  const [health, setHealth] = useState<EnterpriseHealth | null>(null);
  const [brief, setBrief] = useState<ExecutiveBrief | null>(null);
  const [decisions, setDecisions] = useState<ExecutiveDecision[]>([]);
  const [risks, setRisks] = useState<EnterpriseRisk[]>([]);
  const [opportunities, setOpportunities] = useState<BusinessOpportunity[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [alerts, setAlerts] = useState<ExecutiveAlert[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, hlth, br, dec, rsk, opp, tl, al] = await Promise.all([
        executiveCockpitApi.dashboard(role),
        executiveCockpitApi.health(),
        executiveCockpitApi.brief(),
        executiveCockpitApi.listDecisions('pending'),
        executiveCockpitApi.listRisks(),
        executiveCockpitApi.listOpportunities(),
        executiveCockpitApi.timeline(),
        executiveCockpitApi.listAlerts(),
      ]);
      setDashboard(dash);
      setHealth(hlth);
      setBrief(br);
      setDecisions(dec);
      setRisks(rsk);
      setOpportunities(opp);
      setTimeline(tl);
      setAlerts(al);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load executive cockpit');
    }
  }, [role]);

  useEffect(() => { refresh(); }, [refresh]);

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'command', label: 'Command Center' },
    { id: 'brief', label: 'AI Executive Brief' },
    { id: 'decisions', label: 'Decision Center' },
    { id: 'risks', label: 'Risk Center' },
    { id: 'opportunities', label: 'Opportunities' },
    { id: 'approvals', label: 'Approval Center' },
    { id: 'timeline', label: 'Enterprise Timeline' },
    { id: 'health', label: 'Business Health' },
  ];

  const priorityColor = (p: string) => {
    if (p === 'critical') return 'text-red-600 dark:text-red-400';
    if (p === 'high') return 'text-amber-600 dark:text-amber-400';
    return muted;
  };

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Executive Planning Cockpit</h1>
            <p className={`mt-1 text-sm ${muted}`}>Strategic command center — executive decision intelligence, control tower, and AI executive advisor</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-slate-300 dark:border-slate-700">
              {ROLES.map((r) => (
                <button key={r.id} type="button" onClick={() => setRole(r.id)}
                  className={`px-3 py-1.5 text-sm font-medium ${role === r.id ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : ''}`}>{r.label}</button>
              ))}
            </div>
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
            <button type="button" onClick={refresh} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">Refresh</button>
          </div>
        </div>
        {dashboard && health && (
          <p className={`mt-2 text-sm ${muted}`}>
            {role.toUpperCase()} view · Health {dashboard.enterprise_health_score.toFixed(0)} · {health.on_track ? 'On track' : 'Attention required'} · {dashboard.pending_decisions} decisions pending
          </p>
        )}
      </div>
      {error && <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300" role="alert">{error}</div>}
      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${tab === t.id ? 'border-slate-900 text-slate-900 dark:border-slate-100 dark:text-slate-100' : 'border-transparent text-slate-500'}`}>{t.label}</button>
          ))}
        </nav>
      </div>
      <div className="p-6">
        {tab === 'command' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Enterprise Health', value: dashboard.enterprise_health_score.toFixed(0) },
              { label: 'Executive Readiness', value: dashboard.executive_readiness_index.toFixed(0) },
              { label: 'Business Confidence', value: dashboard.business_confidence.toFixed(0) },
              { label: 'Enterprise Readiness', value: `${dashboard.enterprise_readiness.toFixed(0)}%` },
              { label: 'Revenue', value: fmtMoney(dashboard.revenue) },
              { label: 'Profitability', value: `${(dashboard.profitability * 100).toFixed(0)}%` },
              { label: 'Forecast Accuracy', value: `${dashboard.forecast_accuracy.toFixed(1)}%` },
              { label: 'Consensus Score', value: `${dashboard.consensus_score.toFixed(0)}%` },
              { label: 'Cash Position', value: fmtMoney(dashboard.cash_position) },
              { label: 'Working Capital', value: fmtMoney(dashboard.working_capital) },
              { label: 'Pending Decisions', value: dashboard.pending_decisions },
              { label: 'Open Risks', value: dashboard.open_risks },
            ].map((m) => (
              <div key={m.label} className={card}><p className={`text-sm ${muted}`}>{m.label}</p><p className="mt-1 text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
        )}
        {tab === 'brief' && brief && (
          <div className={card}>
            <h2 className="text-lg font-semibold">{brief.title}</h2>
            <p className={`mt-1 text-sm ${muted}`}>Confidence {(brief.confidence_level * 100).toFixed(0)}%</p>
            <p className="mt-4 text-base leading-relaxed">{brief.business_narrative}</p>
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <div>
                <h3 className="font-medium text-red-600 dark:text-red-400">Top Risks</h3>
                <ul className="mt-2 space-y-2">{brief.top_risks.map((r) => <li key={r} className="text-sm">{r}</li>)}</ul>
              </div>
              <div>
                <h3 className="font-medium text-emerald-600 dark:text-emerald-400">Top Opportunities</h3>
                <ul className="mt-2 space-y-2">{brief.top_opportunities.map((o) => <li key={o} className="text-sm">{o}</li>)}</ul>
              </div>
            </div>
            <div className="mt-6">
              <h3 className="font-medium">Action Priorities</h3>
              <ol className="mt-2 list-decimal space-y-1 pl-5">{brief.action_priorities.map((a) => <li key={a} className="text-sm">{a}</li>)}</ol>
            </div>
            <p className={`mt-4 text-sm italic ${muted}`}>{brief.trend_commentary}</p>
          </div>
        )}
        {tab === 'decisions' && decisions.map((d) => (
          <div key={d.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{d.title}</h3>
              <span className={`text-sm font-medium ${priorityColor(d.priority)}`}>{d.priority}</span>
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{d.decision_owner} · Impact {fmtMoney(d.financial_impact)} · Confidence {(d.confidence_score * 100).toFixed(0)}%</p>
            <p className="mt-2 text-sm">{d.expected_outcome}</p>
            <p className={`mt-1 text-sm ${muted}`}>{d.operational_impact}</p>
          </div>
        ))}
        {tab === 'risks' && risks.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{r.title}</h3>
              <span className={`text-sm font-medium ${r.severity === 'high' || r.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`}>{r.severity}</span>
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{r.category.replace(/_/g, ' ')} · {r.owner} · {(r.probability * 100).toFixed(0)}% probability</p>
            <p className="mt-2 text-sm">{r.business_impact}</p>
            {r.mitigation_plan && <p className="mt-1 text-sm text-emerald-600 dark:text-emerald-400">Mitigation: {r.mitigation_plan}</p>}
          </div>
        ))}
        {tab === 'opportunities' && opportunities.map((o) => (
          <div key={o.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{o.title}</h3>
              <span className="text-lg font-semibold text-emerald-600">{fmtMoney(o.estimated_value)}</span>
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{o.type.replace(/_/g, ' ')} · {o.owner} · ROI {o.expected_roi.toFixed(0)}%</p>
            <p className="mt-2 text-sm">{o.description}</p>
          </div>
        ))}
        {tab === 'approvals' && decisions.map((d) => (
          <div key={d.id} className={`${card} mb-4 flex items-center justify-between gap-4`}>
            <div>
              <h3 className="font-medium">{d.title}</h3>
              <p className={`mt-1 text-sm ${muted}`}>{d.decision_owner} · {fmtMoney(d.financial_impact)}</p>
            </div>
            <button type="button" onClick={async () => { await executiveCockpitApi.approve(d.id); refresh(); }}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">Approve</button>
          </div>
        ))}
        {tab === 'timeline' && timeline.map((e) => (
          <div key={e.id} className={`${card} mb-4 flex gap-4`}>
            <div className={`mt-1 h-3 w-3 shrink-0 rounded-full ${e.event_type.includes('risk') ? 'bg-red-500' : e.event_type.includes('decision') || e.event_type.includes('approval') ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            <div>
              <h3 className="font-medium">{e.title}</h3>
              <p className={`mt-1 text-sm ${muted}`}>{e.event_type.replace(/_/g, ' ')} · {e.module} · {new Date(e.occurred_at).toLocaleDateString()}</p>
              {e.description && <p className="mt-1 text-sm">{e.description}</p>}
            </div>
          </div>
        ))}
        {tab === 'health' && health && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Enterprise Health Score', value: health.enterprise_health_score.toFixed(0) },
              { label: 'Executive Readiness Index', value: health.executive_readiness_index.toFixed(0) },
              { label: 'Decision Quality Index', value: health.decision_quality_index.toFixed(0) },
              { label: 'Business Confidence Index', value: health.business_confidence_index.toFixed(0) },
              { label: 'Planning Health Index', value: health.planning_health_index.toFixed(0) },
              { label: 'Critical Risks', value: health.critical_risks },
              { label: 'High-Value Opportunities', value: health.high_value_opportunities },
              { label: 'Decisions Required Today', value: health.decisions_required_today },
              { label: 'Status', value: health.on_track ? 'On Track' : 'Attention Required' },
            ].map((m) => (
              <div key={m.label} className={card}><p className={`text-sm ${muted}`}>{m.label}</p><p className="mt-1 text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
